import {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useState
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ProductServices } from "../../../services/products";
import Paginator from "../../Paginator";
import CategoryBody from "../CategoryBody";
import CategoryHeader from "../CategoryHeader";
import "./styles.scss";
import TopShadow from "../../common/TopShadow";
import { useAsync } from "../../../utils/hooks/useAsync";
import Loading from "../../common/Loading";
import useDevice from "../../../utils/hooks/useDevice";
import CategoryBodyMobile from "../CategoryBodyMobile";

const materialOptions: MultiSelectOption[] = [
  { value: "all", label: "Todos" },
  { value: "amarillo", label: "Oro Amarillo" },
  { value: "rojo", label: "Oro Rojo" },
  { value: "blanco", label: "Oro Blanco" },
  { value: "plata", label: "Plata" },
];

const materialMap = new Map<string, string>();
materialMap.set("plata", "silver");
materialMap.set("oro", "gold");

type Props = {
  subcategoryOptions: MultiSelectOption[];
};

type ProductResponseData = {
  data: Product[];
  size: number;
};

const CategoryContainer: FunctionComponent<Props> = ({
  subcategoryOptions,
}) => {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [isCustom, setIsCustom] = useState<boolean>(false);
  const { isLoading, data, run } = useAsync<
    ProductResponseData,
    any
  >();

  const { isMobile } = useDevice();
  const productsPerPage = isMobile ? 8 : 20;

  const handleParams = (
    value: string,
    filter: Filter | null,
    mode: "multiselect" | "monoselect" | "page"
  ) => {
    const conditionalFunctions = {
      page: () => {
        params.set("page", value);
        setParams(params);
      },
      monoselect: () => {
        const materialValue =
          value.includes("all") || value.includes("plata")
            ? value
            : `oro-${value}`;
        if (filter) params.set(filter, materialValue);
        setParams(params);
      },
      multiselect: {
        valueNotExist: () => {
          if (value != "all") {
            params.append("sub", value);
            setParams(params);
          } else {
            params.delete("sub");
            setParams(params);
          }
        },
        valueExist: () => {
          const filteredValues = params
            .getAll("sub")
            .filter((x) => x !== value);

          if (params.getAll("sub").length <= 1) {
            params.delete("sub");
          } else {
            params.delete("sub");
            filteredValues.forEach((value) => {
              params.append("sub", value);
            });
          }
          setParams(params);
        },
      },
    };

    const checkValueExistence =
      params.getAll("sub").includes(value) === false
        ? "valueNotExist"
        : "valueExist";

    if (mode === "monoselect" || mode === "page") {
      conditionalFunctions[mode]();
    } else {
      conditionalFunctions[mode][checkValueExistence]();
    }
  };

  const handleInitPageParams = () => {
    const initPage = params.get("page");
    if (initPage) return parseInt(initPage);
    else return 1;
  };

  const handleInitFilterParams = () => {
    const keys = params.keys();
    const firstKey = keys.next();
    const paramFilters: ProductFilters = {
      subcategory: [],
      material: [],
    };
    [];

    if (!firstKey.done) {
      // if the  URL parameters exist

      const matchFilter = (
        filterOptions: MultiSelectOption[],
        value: string
      ) => {
        const currentFilter = filterOptions.filter((fo) =>
          value.includes(fo.value)
        );
        return currentFilter[0];
      };

      params.forEach((value, key) => {
        const handleAddFilterOption = (
          filter: MultiSelectOption[],
          options: MultiSelectOption[]
        ) => {
          const filterOption = matchFilter(options, value);
          if (filterOption) {
            filter.push(filterOption);
          }
        };

        switch (key) {
          case "sub":
            handleAddFilterOption(paramFilters.subcategory, subcategoryOptions);
            break;
          case "material":
            handleAddFilterOption(paramFilters.material, materialOptions);
        }
      });
    }
    return paramFilters;
  };

  const [selectedPage, setSelectedPage] = useState<number>(
    handleInitPageParams()
  );

  const [filters, setFilters] = useState<ProductFilters>(
    handleInitFilterParams()
  );

  const [currentMultiSelectFilter, setCurrentMultiSelectFilter] = useState<
    MultiSelectOption[]
  >(handleInitFilterParams().subcategory);

  useEffect(() => {
    const materialFilter =
      filters.material.length > 0 && filters.material[0].value !== "all"
        ? filters.material[0].value
        : undefined;

    const subcategoryFilter: string[] = [];
    filters.subcategory.map((sub) => {
      sub.value !== "all" && subcategoryFilter.push(sub.value);
    });

    const limit = !isMobile ? productsPerPage : selectedPage * productsPerPage;
    const skip = !isMobile ? (selectedPage - 1) * productsPerPage : undefined;

    run(
      ProductServices.getProducts({
        department: category,
        color: materialFilter,
        material: !materialFilter
          ? ""
          : materialMap.get(materialFilter) || "gold",
        subcategory: subcategoryFilter,
        custom: isCustom,
        limit,
        skip,
      })
    );
  }, [filters, selectedPage, isCustom, category]);

  const handleFilterChange = (
    e: ChangeEvent<HTMLInputElement>,
    filter: Filter,
    options: MultiSelectOption[],
    mode: "multiselect" | "monoselect" | "page"
  ) => {
    const { value } = e.target;
    const currentCheckedOption = options.find((o) => o.value === value);

    if (mode === "monoselect") {
      handleParams(value, filter, mode);
      setFilters({ ...filters, [filter]: [currentCheckedOption] });
    } else {
      if (currentCheckedOption) {
        if (value !== "all") {
          if (currentMultiSelectFilter.some((x) => x.value === value)) {
            setCurrentMultiSelectFilter(
              currentMultiSelectFilter.filter((x) => x.value !== value)
            );
          } else {
            setCurrentMultiSelectFilter([
              ...new Set([
                ...currentMultiSelectFilter
                  .filter((f) => f.value !== "all")
                  .concat(currentCheckedOption),
              ]),
            ]);
          }
        } else {
          setCurrentMultiSelectFilter([currentCheckedOption]);
        }
      }
    }
  };

  const handleSelectPage = (page: number) => {
    setSelectedPage(page);
    handleParams(page.toString(), null, "page");
    if (!isMobile) window.scrollTo(0, 0);
  };

  const handleMultiselectFilter = () => {
    params.delete("sub");
    setParams(params);
    currentMultiSelectFilter.forEach((x) => {
      handleParams(x.value, "subcategory", "multiselect");
    });
    setSelectedPage(1);
    setFilters({
      ...filters,
      subcategory: currentMultiSelectFilter,
    });
  };

  return (
    <section className="category">
      <TopShadow />
      // In mobile infinite scroll will show the loading spinner
      {isLoading && isMobile && !data && <Loading />}
      {isLoading && !isMobile && <Loading />}
      {data && data.data.length === 0 && (
        <p className="category__empty-state">No se encontraron resultados.</p>
      )}
      {data && subcategoryOptions && (
        <CategoryHeader
          handleMultiselectFilter={handleMultiselectFilter}
          currentMultiSelectFilter={currentMultiSelectFilter}
          handleFilterChange={handleFilterChange}
          filters={filters}
          materialOptions={materialOptions}
          setFilters={setFilters}
          subcategoryOptions={subcategoryOptions}
          isCustom={isCustom}
          setIsCustom={setIsCustom}
        />
      )}
      {isMobile && data ? (
        <CategoryBodyMobile
          products={data.data}
          selectedPage={selectedPage}
          setSelectedPage={handleSelectPage}
          totalProducts={data.size}
        />
      ) : null}
      {!isMobile && data ? <CategoryBody products={data.data} /> : null}
      {!isMobile && data && data.data.length > 0 && (
        <Paginator
          pages={Math.ceil(data.size / productsPerPage)}
          handleSelectPage={handleSelectPage}
          selectedPage={selectedPage}
        />
      )}
    </section>
  );
};

export default CategoryContainer;
